// "Gridify" mode for `cloudgrid init` (Decision 042 Mode 1 §init behavior b)
// and the `cloudgrid dev` first-run auto-init.
//
// Founder framing (2026-06-28): "I want to migrate/gridify an existing app into
// cloudgrid-compliant but NOT yet plugged — create the yaml, identify 'needs',
// and verify with the user; everything that helps the builder prepare their app
// BEFORE the first plug."
//
// The distinction from `plug` auto-init (services/plug-auto-init.ts) is
// deliberate and load-bearing: gridify does NOT create a server-side entity and
// does NOT write `.cloudgrid/link.json`. It only prepares a local
// `cloudgrid.yaml` the builder can review. The entity is registered on the
// server later, at `cloudgrid plug` time (plug reads the yaml and creates the
// entity when the dir is yaml-present-but-unlinked). Because gridify never
// touches the network, it needs no auth and works offline.
//
// It reuses the existing framework sniffer (framework-detect.ts) for the
// service `type:`, the BL592 auto-detect knowledge base
// (`findMatchingSignatures`) to surface known build pitfalls as a heads-up, and
// the conservative substrate-need mapper (gridify-needs.ts) to propose a
// `needs:` block. Nothing is written without confirmation.

import { existsSync } from 'node:fs';
import { basename, join } from 'node:path';

import inquirer from 'inquirer';

import { CloudGridError } from '../utils.js';
import { appendGitignoreLine } from './link.js';
import { writeGeneratedYaml } from './generated-yaml.js';
import {
  detectFramework as defaultDetectFramework,
  type FrameworkDetection,
  type DetectedType,
} from './framework-detect.js';
import { suggestSlug } from '../commands/slug.js';
import {
  findMatchingSignatures,
  type RepoSnapshot,
} from '../../../shared/src/drop-detection/index.js';
// The node-only snapshot builder is not re-exported from the package index
// (it touches node:fs); import it from its module directly.
import {
  scanFolder,
  readRepoSnapshot,
} from '../../../shared/src/drop-detection/folder-scan.js';
import { inferNeeds, type SubstrateNeed } from './gridify-needs.js';

export interface GridifyOpts {
  /** Skip the confirmation prompt (non-interactive intent — `init --auto`). */
  auto?: boolean;
}

export interface GridifyDeps {
  /** Injectable framework sniffer (tests pass a stub). */
  detect?: (dir: string) => FrameworkDetection | null;
  /** Injectable BL592 repo snapshot builder (tests pass a stub). */
  scan?: (dir: string) => RepoSnapshot;
  /** Injectable yes/no confirm. Defaults to an inquirer confirm. */
  confirm?: (message: string, defaultValue?: boolean) => Promise<boolean>;
  /** Override TTY detection (tests force a value). */
  isTTY?: boolean;
  /** Output sink. Defaults to stdout. */
  writer?: (s: string) => void;
}

/**
 * Outcome of a gridify attempt:
 *   - `created`    — a cloudgrid.yaml was written; the dir is ready to plug.
 *   - `declined`   — the user said no at the confirm prompt; nothing written.
 *   - `exists`     — a cloudgrid.yaml is already present; nothing to do.
 *   - `undetected` — no recognisable app in the folder; caller suggests the
 *                    explicit `cloudgrid init <kind> <slug>` greenfield path.
 */
export type GridifyResult =
  | { kind: 'created'; yamlPath: string; type: DetectedType; needs: SubstrateNeed[]; signatures: string[] }
  | { kind: 'declined' }
  | { kind: 'exists' }
  | { kind: 'undetected' };

/** Default node-only snapshot builder: scan the folder, read its manifests. */
function defaultScan(dir: string): RepoSnapshot {
  return readRepoSnapshot(dir, scanFolder(dir));
}

/**
 * Prepare an existing app for CloudGrid by writing a reviewed `cloudgrid.yaml`.
 * Never registers a server entity and never writes a link — see the module
 * header. The caller decides what to print for the non-`created` outcomes.
 */
export async function runGridify(
  cwd: string,
  opts: GridifyOpts,
  deps: GridifyDeps,
): Promise<GridifyResult> {
  const detect = deps.detect ?? defaultDetectFramework;
  const scan = deps.scan ?? defaultScan;
  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));
  const isTTY = deps.isTTY ?? Boolean(process.stdout.isTTY && process.stdin.isTTY);

  // Never overwrite an existing config. The verb-level callers also guard, but
  // gridify owns the file write so it re-checks right here too.
  if (existsSync(join(cwd, 'cloudgrid.yaml'))) {
    return { kind: 'exists' };
  }

  const detection = detect(cwd);
  if (!detection) {
    return { kind: 'undetected' };
  }

  // Derive the entity slug from the directory name. Gridify is intentionally
  // flag-light: the user reviews and can rename in the yaml before plugging.
  const slug = suggestSlug(basename(cwd));
  if (slug === null) {
    throw new CloudGridError(
      [
        `Couldn't derive a slug from the directory name '${basename(cwd)}'.`,
        'Rename the folder, or scaffold explicitly: cloudgrid init <app|agent> <slug>.',
      ].join('\n'),
    );
  }

  // BL592 knowledge base — surface known build pitfalls for this stack as a
  // heads-up, and the conservative substrate needs as candidates.
  const snapshot = scan(cwd);
  const signatures = findMatchingSignatures(snapshot);
  const needs = inferNeeds(snapshot);

  // Present the plan. §23 voice — plain, specific, no exclamation, no emoji.
  const needsClause = needs.length > 0 ? ` that needs ${needs.join(', ')}` : '';
  writer(`Looks like a ${detection.label}${needsClause}.\n`);
  writer(`  Detected from: ${detection.evidence.join(', ')}\n`);
  if (needs.length > 0) {
    writer(`  Proposed needs: ${needs.join(', ')}\n`);
  }
  for (const sig of signatures) {
    for (const pitfall of sig.hints.common_pitfalls ?? []) {
      writer(`  Heads-up (${sig.id}): ${pitfall.condition} — ${pitfall.explanation}\n`);
    }
  }

  // Confirm unless --auto. Non-interactive runs must opt in: writing a file is a
  // real side effect, and the user should review the inferred needs.
  if (!opts.auto) {
    if (!isTTY) {
      throw new CloudGridError(
        [
          `This folder has no cloudgrid.yaml. I see a ${detection.label}.`,
          'Re-run with --auto to write it without a prompt, or run cloudgrid init <app|agent> <slug>.',
        ].join('\n'),
      );
    }
    const confirm =
      deps.confirm ??
      (async (message: string, defaultValue = true) => {
        const ans = await inquirer.prompt([
          { type: 'confirm', name: 'ok', message, default: defaultValue },
        ]);
        return Boolean(ans.ok);
      });
    const ok = await confirm(`Generate cloudgrid.yaml for '${slug}'?`, true);
    if (!ok) {
      writer('Stopped. Nothing was written. Re-run cloudgrid init when ready.\n');
      return { kind: 'declined' };
    }
  }

  // Re-check the yaml right before the write — a teammate's concurrent write or
  // a git pull during the prompt must not be clobbered.
  if (existsSync(join(cwd, 'cloudgrid.yaml'))) {
    writer('A cloudgrid.yaml appeared while you were deciding. Stopped — nothing was written.\n');
    return { kind: 'exists' };
  }

  writeGeneratedYaml(cwd, {
    name: slug,
    type: detection.type,
    ...(detection.build ? { build: detection.build } : {}),
    ...(needs.length > 0 ? { needs } : {}),
    generatedBy: 'init',
  });
  // The dir stays UNlinked — no .cloudgrid/link.json — but gridignore the
  // local-dev artifacts now so they never get committed once the user plugs.
  appendGitignoreLine('.cloudgrid/', cwd);
  appendGitignoreLine('.cloudgrid-dev.lock', cwd);
  appendGitignoreLine('.env.local', cwd);

  const yamlPath = join(cwd, 'cloudgrid.yaml');
  writer(
    [
      `  Wrote cloudgrid.yaml (${detection.type}, sourced from .)`,
      ...(needs.length > 0 ? [`  Declared needs: ${needs.join(', ')}`] : []),
      '  Not plugged. Review the yaml, then:',
      '',
      '  cloudgrid plug    # register on the grid and deploy',
      '',
    ].join('\n') + '\n',
  );

  return {
    kind: 'created',
    yamlPath,
    type: detection.type,
    needs,
    signatures: signatures.map((s) => s.id),
  };
}
